import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { ChevronRight, X, Camera } from 'lucide-react';
import { AdminUserFilters } from '@/hooks/useAdminFilters';
import { motion, AnimatePresence } from 'framer-motion';

interface AdminFiltersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: AdminUserFilters;
  onUpdateFilter: <K extends keyof AdminUserFilters>(key: K, value: AdminUserFilters[K]) => void;
  onClearFilters: () => void;
}

type FilterCategory = 
  | 'gender'
  | 'orientation'
  | 'role'
  | 'verificationLevel'
  | 'planTypes'
  | 'onboardingStatus'
  | 'hiddenStatus'
  | 'userRoles';

interface FilterOption {
  id: string;
  label: string;
  category: FilterCategory;
  icon?: string;
}

const filterCategories: { id: FilterCategory; label: string; icon: string }[] = [
  { id: 'gender', label: 'Gênero', icon: '👤' },
  { id: 'orientation', label: 'Orientação', icon: '💝' },
  { id: 'role', label: 'Papel', icon: '🎭' },
  { id: 'verificationLevel', label: 'Verificação', icon: '✓' },
  { id: 'planTypes', label: 'Plano', icon: '💎' },
  { id: 'onboardingStatus', label: 'Onboarding', icon: '📋' },
  { id: 'hiddenStatus', label: 'Visibilidade', icon: '👁️' },
  { id: 'userRoles', label: 'Roles de Sistema', icon: '👑' }
];

const categoryOptions: Record<FilterCategory, FilterOption[]> = {
  gender: [
    { id: 'male', label: 'Masculino', category: 'gender' },
    { id: 'female', label: 'Feminino', category: 'gender' },
    { id: 'non-binary', label: 'Não-binário', category: 'gender' }
  ],
  orientation: [
    { id: 'straight', label: 'Hétero', category: 'orientation' },
    { id: 'gay', label: 'Gay', category: 'orientation' },
    { id: 'lesbian', label: 'Lésbica', category: 'orientation' },
    { id: 'bisexual', label: 'Bissexual', category: 'orientation' }
  ],
  role: [
    { id: 'sponsor', label: 'Sponsor', category: 'role' },
    { id: 'sponsored', label: 'Sponsored', category: 'role' }
  ],
  verificationLevel: [
    { id: 'basic', label: 'Básica', category: 'verificationLevel' },
    { id: 'waiting', label: 'Aguardando', category: 'verificationLevel' },
    { id: 'full', label: 'Completa', category: 'verificationLevel' }
  ],
  planTypes: [
    { id: 'free', label: 'Free', category: 'planTypes' },
    { id: 'sponsor_free', label: 'Sponsor Free', category: 'planTypes' },
    { id: 'sponsor_standard', label: 'Sponsor Standard', category: 'planTypes' },
    { id: 'sponsor_premium', label: 'Sponsor Premium', category: 'planTypes' },
    { id: 'sponsored_free', label: 'Sponsored Free', category: 'planTypes' },
    { id: 'sponsored_premium', label: 'Sponsored Premium', category: 'planTypes' }
  ],
  onboardingStatus: [
    { id: 'completed', label: 'Completo', category: 'onboardingStatus' },
    { id: 'incomplete', label: 'Incompleto', category: 'onboardingStatus' }
  ],
  hiddenStatus: [
    { id: 'visible', label: 'Visível', category: 'hiddenStatus' },
    { id: 'hidden', label: 'Oculto', category: 'hiddenStatus' }
  ],
  userRoles: [
    { id: 'admin', label: 'Administrador', category: 'userRoles' },
    { id: 'moderator', label: 'Moderador', category: 'userRoles' },
    { id: 'user', label: 'Usuário', category: 'userRoles' }
  ]
};

const AdminFiltersModal: React.FC<AdminFiltersModalProps> = ({
  open,
  onOpenChange,
  filters,
  onUpdateFilter,
  onClearFilters
}) => {
  const [activeCategory, setActiveCategory] = useState<FilterCategory | 'main'>('main');

  const getTotalFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) count++;
      else if (key === 'hasPhotos' && value === true) count++;
    });
    return count;
  };

  const getCategoryFilterCount = (category: FilterCategory) => {
    const value = filters[category];
    return Array.isArray(value) ? value.length : 0;
  };

  const handleToggleOption = (category: FilterCategory, optionId: string) => {
    const currentValues = (filters[category] as string[]) || [];
    const newValues = currentValues.includes(optionId)
      ? currentValues.filter(v => v !== optionId)
      : [...currentValues, optionId];
    onUpdateFilter(category, newValues.length > 0 ? newValues : undefined);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 bg-black border-white/10">
        <AnimatePresence mode="wait">
          {activeCategory === 'main' ? (
            <motion.div
              key="main"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -20, opacity: 0 }}
              className="h-full flex flex-col"
            >
              <SheetHeader className="p-6 pb-4">
                <SheetTitle className="text-white">Filtros Avançados</SheetTitle>
                <SheetDescription className="text-white/60">
                  {getTotalFilterCount()} filtros ativos
                </SheetDescription>
              </SheetHeader>

              <ScrollArea className="flex-1 px-6">
                <div className="space-y-4 pb-6">
                  {/* Quick Toggles Section */}
                  <div className="space-y-2">
                    <p className="text-xs text-white/40 uppercase tracking-wide font-medium">Filtros Rápidos</p>
                    <label className="flex items-center justify-between p-4 rounded-lg bg-white/5 cursor-pointer">
                      <div className="flex items-center gap-3">
                        <Camera className="h-5 w-5 text-white/60" />
                        <span className="text-white font-medium">Somente com fotos</span>
                      </div>
                      <Switch
                        checked={filters.hasPhotos || false}
                        onCheckedChange={(checked) => onUpdateFilter('hasPhotos', checked || undefined)}
                      />
                    </label>
                  </div>

                  {/* Category Filters */}
                  <div className="space-y-2">
                    <p className="text-xs text-white/40 uppercase tracking-wide font-medium">Categorias</p>
                    {filterCategories.map(category => {
                      const count = getCategoryFilterCount(category.id);
                      return (
                        <button
                          key={category.id}
                          onClick={() => setActiveCategory(category.id)}
                          className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{category.icon}</span>
                            <span className="text-white font-medium">{category.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {count > 0 && (
                              <span className="px-2 py-0.5 rounded-full bg-primary text-xs font-medium">
                                {count}
                              </span>
                            )}
                            <ChevronRight className="h-5 w-5 text-white/40" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </ScrollArea>

              <div className="p-6 pt-4 border-t border-white/10 space-y-2">
                <Button
                  onClick={onClearFilters}
                  variant="outline"
                  className="w-full"
                  disabled={getTotalFilterCount() === 0}
                >
                  Limpar Filtros
                </Button>
                <Button onClick={() => onOpenChange(false)} className="w-full">
                  Aplicar Filtros
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={activeCategory}
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 20, opacity: 0 }}
              className="h-full flex flex-col"
            >
              <SheetHeader className="p-6 pb-4">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setActiveCategory('main')}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <SheetTitle className="text-white">
                    {filterCategories.find(c => c.id === activeCategory)?.label}
                  </SheetTitle>
                </div>
              </SheetHeader>

              <ScrollArea className="flex-1 px-6">
                <div className="space-y-3 pb-6">
                  {categoryOptions[activeCategory]?.map(option => {
                    const isChecked = (filters[activeCategory] as string[])?.includes(option.id);
                    return (
                      <label
                        key={option.id}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => handleToggleOption(activeCategory, option.id)}
                        />
                        <span className="text-white">{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </ScrollArea>

              <div className="p-6 pt-4 border-t border-white/10">
                <Button onClick={() => setActiveCategory('main')} className="w-full">
                  Voltar
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </SheetContent>
    </Sheet>
  );
};

export default AdminFiltersModal;
